import { QueryClient } from "@tanstack/react-query"

import type { TTFunction } from "$/i18n/i18n"

import type { TAvatarListUser, TAvatarProps } from "@/3514/components"
import type { TScreenBreakpoints } from "@/3514/hooks"
import type { ICoachPaginatedSessionModel as ISession } from "@/3514/types"
import type { IUseTranslation } from "@/hooks"
import type { TEmptyAsyncCallback, TEmptyCallback } from "@/shared/types/functions"

import type {
    TCoachDashboardParticipantsQuery as TParticipantsQuery,
    TCoachDashboardSessionsQuery as TSessionsQuery
} from "./coach.dashboard.api.queries.hooks"
import type {
    ICoachSessionWithoutAttendanceMarksModel as IAttendances,
    ICoachDashboardParticipantsQueryParticipantModel as IParticipant,
    ICoachDashboardParticipantsQueryModel as IParticipantsQueryModel,
    ICoachDashboardSessionsQueryModel as ISessionsQueryModel,
    ICoachDashboardParticipantsQueryParticipantDto as IUserParticipantDto,
    TCoachDashboardParticipantsQueryKeyPayload as TParticipantsKeyPayload,
    TCoachDashboardSessionsQueryKeyPayload as TSessionsKeyPayload
} from "./coach.dashboard.api.types"

type TCoachMarkAttendanceState = {
    withExpandButton: boolean
    isListExpanded: boolean
    visibleItems: IAttendances[]
}

enum ECoachDashboardActiveTab {
    Sessions = "sessions",
    Participants = "participants"
}

enum ECoachDashboardPeriodFilter {
    UpcomingSessions = "upcoming_sessions",
    PastSessions = "past_sessions"
}

enum ECoachDashboardStaticNotificationType {
    SetUpCalendar = "setupCalendar",
    RelinkCalendar = "relinkCalendar"
}

type TCoachDashboardStaticNotificationConfig = {
    isVisible: boolean
    withTitleUppercased?: boolean
    title: string
    description: string
    actionLabel: string
    action: TEmptyCallback
    hover?: TEmptyAsyncCallback
}

type TCoachDashboardRenderStaticNotificationConfigFunctionParams = {
    getIsBoxVisible(type: ECoachDashboardStaticNotificationType): boolean
    action(type: ECoachDashboardStaticNotificationType): void
    qc: QueryClient
    t: TTFunction
}

type TCoachDashboardRenderStaticNotificationConfigFunction = (
    params: TCoachDashboardRenderStaticNotificationConfigFunctionParams
) => TCoachDashboardStaticNotificationConfig

type TCoachDashboardStaticNotificationsMap = {
    [K in ECoachDashboardStaticNotificationType]: TCoachDashboardRenderStaticNotificationConfigFunction
}

type TCoachDashboardStaticNotificationHandlersMap = { [K in ECoachDashboardStaticNotificationType]: TEmptyCallback }

type TCoachDashboardGetIsStaticNotificationVisibleFunction = (type: ECoachDashboardStaticNotificationType) => boolean

enum ECoachDashboardTableColumnName {
    Sessions_DateTime = "s_dateTime",
    Sessions_Cohort = "s_cohort",
    Sessions_SessionType = "s_sessionType",
    Sessions_Participants = "s_participants",
    Participants_Avatar = "p_avatar",
    Participants_Name = "p_name",
    Participants_Cohort = "p_cohort",
    Participants_Program = "p_program",
    Participants_Sessions = "p_sessions"
}

type TCoachDashboardTableHeader = {
    name: ECoachDashboardTableColumnName
    label: string | null
    isSortable: boolean
    isVisible: boolean
}

type TCoachDashboardRenderTableHeaderFunction = () => TCoachDashboardTableHeader

type TCoachDashboardTableHeadersRenderMap = {
    [T in ECoachDashboardActiveTab]: (params: {
        breakpoints: TScreenBreakpoints
        translation: IUseTranslation
    }) => TCoachDashboardRenderTableHeaderFunction[]
}

enum ECoachDashboardTableDataType {
    Sessions = "sessions",
    Participants = "participants"
}

type TCoachDashboardTableRowDataMap = {
    [ECoachDashboardTableDataType.Sessions]: ISession
    [ECoachDashboardTableDataType.Participants]: IParticipant
}

type TCoachDashboardTableRowDataIndexKeysMap = {
    [ECoachDashboardTableDataType.Sessions]: keyof ISession
    [ECoachDashboardTableDataType.Participants]: keyof IParticipant
}

type TCoachDashboardTableQueryDataMap = {
    [ECoachDashboardTableDataType.Sessions]: ISessionsQueryModel
    [ECoachDashboardTableDataType.Participants]: IParticipantsQueryModel
}

type TCoachDashboardTableTypeQueryHooksRefMap = {
    [ECoachDashboardTableDataType.Sessions]: (key: TSessionsKeyPayload) => TSessionsQuery
    [ECoachDashboardTableDataType.Participants]: (key: TParticipantsKeyPayload) => TParticipantsQuery
}

type TCoachDashboardTableTypeQueryHooksMap = {
    [ECoachDashboardTableDataType.Sessions]: TSessionsQuery
    [ECoachDashboardTableDataType.Participants]: TParticipantsQuery
}

type TCoachDashboardTableTypeQueryKeysMap = {
    [ECoachDashboardTableDataType.Sessions]: TSessionsKeyPayload
    [ECoachDashboardTableDataType.Participants]: TParticipantsKeyPayload
}

enum ECoachDashboardTableCellType {
    AccentText = "accentText",
    SingleText = "singleTest",
    DetailedText = "detailedText",
    Avatars = "avatars",
    SingleAvatar = "singleAvatar"
}

/**
 * NB: plain values, never hooks. The column config is executed per table row inside the
 * table body render, so anything passed here must be data - calling a hook from there
 * makes the hook count of the body component depend on the row count and breaks React's
 * hook order as soon as the row count or the active tab changes.
 */
type TCoachDashboardTableColumnRenderConfigParams = {
    breakpoints: TScreenBreakpoints
    translation: IUseTranslation
}

type _TCoachDashboardTableCellSharedProps = {
    index?: number
    isVisible: boolean
} & TCoachDashboardTableColumnRenderConfigParams

type TCoachDashboardTableCellProps =
    | ({ type: ECoachDashboardTableCellType.AccentText } & {
          title: string
          subTitle: string
      } & _TCoachDashboardTableCellSharedProps)
    | ({ type: ECoachDashboardTableCellType.SingleText } & { subTitle: string } & _TCoachDashboardTableCellSharedProps)
    | ({ type: ECoachDashboardTableCellType.DetailedText } & {
          title: string
          subTitle: string
      } & _TCoachDashboardTableCellSharedProps)
    | ({ type: ECoachDashboardTableCellType.Avatars } & {
          participants: TAvatarListUser[]
          sessionJoinUrl: string | undefined
          sessionDuration: number
          sessionTime: string
      } & _TCoachDashboardTableCellSharedProps)
    | ({ type: ECoachDashboardTableCellType.SingleAvatar } & Pick<TAvatarProps, "userData" | "url"> &
          _TCoachDashboardTableCellSharedProps)

type TCoachDashboardTableRowProps<TData, TIndexKey extends keyof TData> = {
    index?: number
    dataSource: TData
    cellPropsRenderers: TCoachDashboardTableCellProps[]
    type: ECoachDashboardTableDataType
    indexKey?: TIndexKey
    animationTimeout?: number
}

type TCoachDashboardTableColumnConfigRenderer<TData> = (data: TData) => TCoachDashboardTableCellProps

type TCoachDashboardTableRowRenderConfigsMap = {
    [ECoachDashboardTableDataType.Sessions]: <TData extends ISession = ISession>(props: {
        data: TData
        columnsRenderers: TCoachDashboardTableColumnConfigRenderer<TData>[]
    }) => TCoachDashboardTableRowProps<TData, "sessionId">
    [ECoachDashboardTableDataType.Participants]: <TData extends IParticipant = IParticipant>(props: {
        data: TData
        columnsRenderers: TCoachDashboardTableColumnConfigRenderer<TData>[]
    }) => TCoachDashboardTableRowProps<TData, "id">
}

type TCoachDashboardTableColumnRenderConfigsMap = {
    [ECoachDashboardTableDataType.Sessions]: (
        params: TCoachDashboardTableColumnRenderConfigParams
    ) => ((
        data: TCoachDashboardTableRowDataMap[ECoachDashboardTableDataType.Sessions]
    ) => TCoachDashboardTableCellProps)[]
    [ECoachDashboardTableDataType.Participants]: (
        params: TCoachDashboardTableColumnRenderConfigParams
    ) => ((
        data: TCoachDashboardTableRowDataMap[ECoachDashboardTableDataType.Participants]
    ) => TCoachDashboardTableCellProps)[]
}

type TCoachDashboardTableSortType = "asc" | "desc"

type TCoachDashboardTableSortConfig = { colName: ECoachDashboardTableColumnName; type: TCoachDashboardTableSortType }

type TCoachDashboardTableSorter = (dtos: IUserParticipantDto[]) => IUserParticipantDto[]

type TCoachDashboardTableSortersMap = {
    [C in ECoachDashboardTableColumnName.Participants_Name | ECoachDashboardTableColumnName.Participants_Sessions]: {
        [sort in TCoachDashboardTableSortType]: TCoachDashboardTableSorter
    }
}

type TCoachDashboardPaginationRangeGetterFunctionParams = {
    totalCount: number
    currentPage: number
    filler: string
    pageSize?: number
    siblingCount?: number
}
type TCoachDashboardPaginationRangeGetterFunction = (
    params: TCoachDashboardPaginationRangeGetterFunctionParams
) => (string | number)[]

export {
    ECoachDashboardActiveTab,
    ECoachDashboardStaticNotificationType,
    ECoachDashboardPeriodFilter,
    ECoachDashboardTableColumnName,
    ECoachDashboardTableDataType,
    ECoachDashboardTableCellType
}
export type {
    TCoachDashboardTableHeader,
    TCoachDashboardRenderTableHeaderFunction,
    TCoachDashboardTableHeadersRenderMap,
    TCoachMarkAttendanceState,
    TCoachDashboardStaticNotificationConfig,
    TCoachDashboardRenderStaticNotificationConfigFunctionParams,
    TCoachDashboardRenderStaticNotificationConfigFunction,
    TCoachDashboardStaticNotificationsMap,
    TCoachDashboardStaticNotificationHandlersMap,
    TCoachDashboardGetIsStaticNotificationVisibleFunction,
    TCoachDashboardTableRowDataMap,
    TCoachDashboardTableQueryDataMap,
    TCoachDashboardTableCellProps,
    TCoachDashboardTableRowProps,
    TCoachDashboardTableColumnConfigRenderer,
    TCoachDashboardTableColumnRenderConfigParams,
    TCoachDashboardTableColumnRenderConfigsMap,
    TCoachDashboardTableRowRenderConfigsMap,
    TCoachDashboardTableSortType,
    TCoachDashboardTableSortersMap,
    TCoachDashboardTableSortConfig,
    TCoachDashboardTableSorter,
    TCoachDashboardPaginationRangeGetterFunctionParams,
    TCoachDashboardPaginationRangeGetterFunction,
    TCoachDashboardTableTypeQueryKeysMap,
    TCoachDashboardTableTypeQueryHooksMap,
    TCoachDashboardTableTypeQueryHooksRefMap,
    TCoachDashboardTableRowDataIndexKeysMap
}
